import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useT } from '../i18n/locale';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-600 border-transparent',
  secondary: 'bg-white text-slate-700 hover:bg-slate-100 border-slate-300',
  danger: 'bg-white text-red-600 hover:bg-red-50 border-red-200',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 border-transparent',
};

/** Ref-forwarding so dialogs can focus the right button on open. */
export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }
>(function Button({ variant = 'primary', className = '', ...props }, ref) {
  return (
    <button
      {...props}
      ref={ref}
      className={`inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-2.5
        text-sm font-bold shadow-sm transition-colors
        disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTS[variant]} ${className}`}
    />
  );
});

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold tracking-wide text-slate-500">{label}</span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs font-semibold text-red-600">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-slate-400">{hint}</span>
      ) : null}
    </label>
  );
}

/**
 * Ref-forwarding matters here: the scan field is focus-managed for a USB
 * barcode wedge, so the parent must be able to reach the real input node.
 */
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', ...props }, ref) {
    return (
      <input
        {...props}
        ref={ref}
        className={`w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm
          font-semibold text-slate-800 shadow-sm placeholder:font-normal placeholder:text-slate-400
          focus:border-brand-500 focus:outline-none ${className}`}
      />
    );
  },
);

/**
 * A password field with a reveal toggle.
 *
 * Used for every password input in the app. Typing a long password blind and
 * getting it wrong is how people end up choosing something short, and an
 * administrator setting a password for somebody else has to be able to read
 * it back accurately in order to hand it over.
 *
 * An icon rather than a word: it sits inside the field, so it has to stay out
 * of the way of the value, and it needs no translating.
 */
export const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function PasswordInput({ className = '', ...props }, ref) {
    const [shown, setShown] = useState(false);
    const t = useT();

    return (
      <div className="relative">
        <Input
          {...props}
          ref={ref}
          type={shown ? 'text' : 'password'}
          // Room for the toggle, so a long password never runs underneath it.
          className={`pr-11 ${className}`}
        />
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-label={shown ? t('ui.hidePassword') : t('ui.showPassword')}
          aria-pressed={shown}
          title={shown ? t('ui.hide') : t('ui.show')}
          // Keeps focus in the field: clicking this should not look like
          // leaving it, and a blur would dismiss any validation message.
          onMouseDown={(e) => e.preventDefault()}
          className="absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer rounded-md border-0
            bg-transparent p-2 text-slate-400 hover:text-brand-600"
        >
          <EyeIcon off={shown} />
        </button>
      </div>
    );
  },
);

/** Open eye, or struck through when the value is already visible. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="block"
    >
      <path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3.2" />
      {off ? <line x1="3" y1="21" x2="21" y2="3" /> : null}
    </svg>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
      {children}
    </div>
  );
}

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'error' | 'success';
  children: ReactNode;
}) {
  const tones = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    error: 'bg-red-50 border-red-200 text-red-700',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  } as const;

  return (
    <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${tones[tone]}`}>
      {children}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-slate-400">
      <div
        className="h-8 w-8 animate-spin rounded-full border-3 border-slate-200 border-t-brand-500"
        role="status"
        aria-label={t('common.loading')}
      />
      {label ? <p className="text-sm font-semibold">{label}</p> : null}
    </div>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width = 'max-w-lg',
  dismissable = true,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
  /** False while an action is in flight, so a stray Escape cannot abandon it. */
  dismissable?: boolean;
}) {
  const t = useT();
  // Escape closes. Native dialogs do this and people expect it; without it a
  // modal with a disabled close button can feel like a trap.
  useEffect(() => {
    if (!open || !dismissable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismissable, onClose]);

  if (!open) return null;
  // Portaled to the body so the overlay is always positioned against the
  // viewport. A transformed ancestor (e.g. the page-transition wrapper)
  // otherwise becomes the containing block for this `fixed` overlay and
  // confines it to the page content's box instead of the whole screen.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      /*
       * No close-on-backdrop, deliberately.
       *
       * It cost people their work. Selecting text in a field by dragging —
       * which is how you select a whole password or email — often releases
       * the mouse outside the panel, and a click event then fires on the
       * backdrop with the backdrop as its target. The dialog closed and
       * everything typed into it was gone, in the middle of an action the
       * person was plainly still doing.
       *
       * Escape and the close button remain, so there is no way to get stuck,
       * and both are deliberate in a way that a stray mouse-up is not.
       */
    >
      <div className={`flex max-h-[92vh] w-full ${width} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl`}>
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3">
          <h3 className="m-0 text-base font-bold text-slate-800">{title}</h3>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="cursor-pointer border-0 bg-transparent text-xl leading-none text-slate-400 hover:text-red-500"
          >
            &times;
          </button>
        </div>
        <div className="scroll-slim flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/** Quota bar shown in the header — the SaaS limit made visible before it bites. */
export function UsageBar({ used, limit, warn }: { used: number; limit: number; warn: boolean }) {
  const t = useT();
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
  const tone = pct >= 100 ? 'bg-red-500' : warn ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="min-w-44">
      <div className="mb-1 flex justify-between text-[11px] font-bold text-white/70">
        <span>{t('app.quotaLeft')}</span>
        <span>
          {used.toLocaleString('id-ID')} / {limit.toLocaleString('id-ID')}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/20">
        <div className={`h-full rounded-full transition-all ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * A compact "⋮" trigger that opens a menu of row actions.
 *
 * The menu renders in a portal with fixed positioning, because the table it
 * lives in scrolls and clips its own overflow — an absolutely-positioned menu
 * inside would be cut off. Repositioning while that container scrolls is more
 * trouble than it is worth, so the menu simply closes on scroll or resize; the
 * trigger is still there to reopen. Closes on an outside click or Escape.
 */
export function ActionMenu({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    };
    place();
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className="cursor-pointer rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-lg leading-none text-slate-600 hover:bg-slate-100"
      >
        ⋮
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ position: 'fixed', top: pos.top, right: pos.right }}
              className="z-50 min-w-40 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-xl"
              onClick={() => setOpen(false)}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export function ActionMenuItem({
  onClick,
  disabled = false,
  tone = 'default',
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`block w-full cursor-pointer border-0 bg-transparent px-3 py-2 text-left text-sm font-semibold disabled:cursor-default disabled:opacity-40 ${
        tone === 'danger' ? 'text-red-600 hover:bg-red-50' : 'text-slate-700 hover:bg-slate-100'
      }`}
    >
      {children}
    </button>
  );
}

/** A plain download glyph, for icon-only download controls. */
export function DownloadIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}
